import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import { hasCallToAction, makeImagePreviewable } from '../utils';

import Block from './Block';
import H2 from './H2';
import RichTextHtml from './RichTextHtml';
import Cell from './Cell';
import Grid from './Grid';
import Image from './Image';
import CTAAnchor from './CTAAnchor';
import MediaDisplay from './MediaDisplay';

const ImageWrapper = styled.div`
  display: none;
  width: 50%;
  position: absolute;
  top: 0;
  ${({ isReversed }) => (isReversed ? 'right: 0;' : 'left: 0;')}
  bottom: 0;
  overflow: hidden;

  ${breakpoint('tablet')`
    display: flex;

    > div {
      flex: 1
    }
  `}
`;

const PageTextMedia = ({ content }) => {
  const image = makeImagePreviewable(content.image);
  const hasImage = Boolean(image.localFile);

  const isDarkTheme =
    content.background_color === 'blueDarker' ||
    content.background_color === 'blueDark';

  return (
    <Block
      id={content.internal_link_id}
      backgroundColor={content.background_color}
      position="relative"
    >
      {hasImage && (
        <ImageWrapper isReversed={content.media_position === 'right'}>
          <Image content={image} style={{ width: '100%' }} />
        </ImageWrapper>
      )}

      <Grid
        flexDirection={{
          tablet: content.media_position === 'right' ? 'row-reverse' : 'row',
        }}
        valign="center"
      >
        <Cell size={{ mobile: 1, tablet: 1 / 2 }}>
          <Block
            px="baseUnit"
            pt={{ mobile: 'baseUnit', tablet: 'baseUp12' }}
            pb={{ mobile: 'baseUnit', tablet: 'baseUp12' }}
          >
            <MediaDisplay content={content} />
          </Block>
        </Cell>
        <Cell
          size={{ mobile: 1, tablet: 1 / 2 }}
          py={{ mobile: 'baseUp4', tablet: 'baseUp12' }}
        >
          <Block px="baseUnit">
            <H2 mb="baseUnit" color={isDarkTheme ? 'coralLight' : 'blueDark'}>
              {content.title1.text}
            </H2>

            <RichTextHtml
              html={content.text.html}
              color={isDarkTheme ? 'white' : 'black'}
            />

            {hasCallToAction(content) ? (
              <Block pt="baseUp4">
                <CTAAnchor
                  text={content.call_to_action_text.text}
                  link={content.call_to_action_link}
                  colorTheme={content.call_to_action_color}
                />
              </Block>
            ) : null}
          </Block>
        </Cell>
      </Grid>
    </Block>
  );
};

PageTextMedia.propTypes = {
  content: PropTypes.shape({
    internal_link_id: PropTypes.string,
    title1: PropTypes.shape({ text: PropTypes.string }),
    text: PropTypes.shape({ html: PropTypes.string }),
    video: PropTypes.shape({
      title: PropTypes.string,
      embed_url: PropTypes.string,
      provider_name: PropTypes.string,
    }),
    amazon_video_path: PropTypes.string,
    image: PropTypes.shape({
      alt: PropTypes.string,
      localFile: PropTypes.shape({
        extension: PropTypes.string,
        url: PropTypes.string,
        childImageSharp: PropTypes.shape({ fluid: PropTypes.shape({}) }),
      }),
    }),
    background_color: PropTypes.string,
    media_position: PropTypes.string,
    call_to_action_color: PropTypes.string,
    call_to_action_text: PropTypes.shape({ text: PropTypes.string }),
    call_to_action_link: PropTypes.shape({
      url: PropTypes.string,
      link_type: PropTypes.string,
      lang: PropTypes.string,
      target: PropTypes.string,
    }),
  }).isRequired,
};

export default PageTextMedia;

export const query = graphql`
  fragment PageTextMediaFragment on PrismicPageBodyTextMedia {
    id
    slice_type
    primary {
      internal_link_id
      title1 {
        text
      }
      text {
        html
      }
      video {
        title
        embed_url
        provider_name
      }
      amazon_video_path
      image {
        alt
        localFile {
          childImageSharp {
            fluid(maxWidth: 550) {
              ...GatsbyImageSharpFluid
            }
          }
        }
      }
      background_color
      media_position
      call_to_action_color
      call_to_action_text {
        text
      }
      call_to_action_link {
        link_type
        url
        lang
        target
      }
    }
  }
`;
